import { useQuery } from "@tanstack/react-query";
import { Navigate } from "react-router-dom";
import { ExternalLink, Newspaper, Radio, Mic } from "lucide-react";
import { useSeo } from "@/lib/useSeo";
import { getList } from "@/lib/api";
import { IMG } from "@/lib/images";
import { PageHeader } from "@/components/Editorial";

export default function Presse() {
  const { data: press = [], isLoading } = useQuery({ queryKey: ["press"], queryFn: () => getList("press") });

  useSeo({
    title: "Presse & Médias",
    description: "Dorothée Lancelot dans les médias : articles de presse, interviews, passages radio, publications et interventions publiques.",
    path: "/presse-medias",
  });

  if (!isLoading && press.length === 0) return <Navigate to="/" replace />;

  return (
    <div>
      <PageHeader
        eyebrow="Revue de presse"
        title="Presse & Médias"
        intro="Le travail de Dorothée Lancelot a fait l'objet d'articles de presse, d'interviews et de passages radio. Retrouvez ici ses apparitions publiques."
        image={IMG.ledger}
      />

      <section className="dl-container py-16">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-16">
          {[
            { Icon: Newspaper, t: "Presse", d: "Articles et publications." },
            { Icon: Mic, t: "Interviews", d: "Rencontres et entretiens." },
            { Icon: Radio, t: "Radio & Médias", d: "Passages et diffusions." },
          ].map(({ Icon, t, d }, i) => (
            <div key={i} className="dl-card p-8">
              <Icon size={22} className="text-[#C6A76A] mb-4" />
              <h3 className="serif text-2xl text-[#F3E9D2]">{t}</h3>
              <p className="text-sm text-[#F3E9D2]/60 mt-2">{d}</p>
            </div>
          ))}
        </div>

        {press.length > 0 && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {press.map((p) => (
              <div key={p.id} className="dl-card overflow-hidden" data-testid={`press-${p.id}`}>
                {p.image_url && <img src={p.image_url} alt={p.title} className="w-full h-48 object-cover" loading="lazy" />}
                <div className="p-7">
                  <div className="flex items-center gap-3">
                    {p.media && <span className="dl-eyebrow" style={{ fontSize: "0.6rem" }}>{p.media}</span>}
                    {p.date && <span className="text-xs text-[#F3E9D2]/50">{p.date}</span>}
                  </div>
                  <h3 className="serif text-xl text-[#F3E9D2] mt-2">{p.title}</h3>
                  {p.excerpt && <p className="text-sm text-[#F3E9D2]/70 mt-3">{p.excerpt}</p>}
                  {p.link && <a href={p.link} target="_blank" rel="noreferrer" className="dl-link text-sm mt-4 inline-flex items-center gap-1">Consulter <ExternalLink size={12} /></a>}
                </div>
              </div>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}
